import { useEffect, useRef, useState } from "react";
import type {
  GeoJSONFeature,
  GeoJSONFeatureCollection,
  GoogleSolarInsights,
} from "@/api/types";
import { useRoofOutlineReview } from "@/api/hooks";
import CardinalCompass from "@/components/quote/CardinalCompass";
import LimeSpinner from "@/components/quote/LimeSpinner";
import {
  geoJsonPolygons,
  latLngToStaticMapPixel,
  parseStaticMapSize,
  parseStaticMapView,
  staticMapUrlWithoutMarkers,
  type LatLng,
  type Pixel,
} from "@/utils/solarLayoutOverlay";
import type { MeshGroundFrame, MeshWorldCenter } from "@/utils/satelliteGroundPlane";
import { drawPanelQuads, panelFootprintPixels } from "@/utils/satellitePanelOverlay";
import type { PlacedRoofPanel } from "@/utils/roofMeshPanelPlacement";

interface SolarPanelMockupProps {
  insights: GoogleSolarInsights;
  propertyBoundary?: GeoJSONFeature;
  viableRoofPolygons?: GeoJSONFeatureCollection;
  satelliteImageUrl?: string | null;
  mapCenter?: LatLng | null;
  mapZoom?: number | null;
  mapSize?: string | null;
  className?: string;
  address?: string;
  postcode?: string;
  outlineSource?: string | null;
  panels?: PlacedRoofPanel[] | null;
  groundAlign?: MeshWorldCenter | null;
  groundFrame?: MeshGroundFrame | null;
}

type Projector = (lat: number, lng: number) => Pixel;

const TITLE_STROKE = "#dc2626";
const TITLE_FILL = "rgba(220, 38, 38, 0.08)";

/**
 * Satellite overlay of where panels can go.
 *
 * Google Solar rgb_url is a GeoTIFF (not browser-displayable). The quote flow
 * already has a Google Static Maps satellite photo — that is the background.
 * Red is the resolved INSPIRE property boundary when the title lookup has one.
 * A missing title is a finished result, so the satellite photo is still shown.
 */
export default function SolarPanelMockup({
  insights,
  propertyBoundary,
  viableRoofPolygons,
  satelliteImageUrl,
  mapCenter,
  mapZoom,
  mapSize,
  className,
  address,
  postcode,
  outlineSource,
  panels,
  groundAlign,
  groundFrame,
}: SolarPanelMockupProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [loadedUrl, setLoadedUrl] = useState("");
  const [failedUrl, setFailedUrl] = useState("");
  const [reviewVerdict, setReviewVerdict] = useState<"" | "positive" | "negative">("");
  const [reviewError, setReviewError] = useState("");
  const review = useRoofOutlineReview();

  const hasTitleBoundary = Boolean(propertyBoundary?.geometry);
  const viableRoofCount = viableRoofPolygons?.features?.length || 0;
  const photoUrl = satelliteImageUrl?.trim()
    ? staticMapUrlWithoutMarkers(satelliteImageUrl.trim())
    : "";
  const imageStatus: "idle" | "loaded" | "error" = !photoUrl
    ? "idle"
    : failedUrl === photoUrl
      ? "error"
      : loadedUrl === photoUrl
        ? "loaded"
        : "idle";

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = imgRef.current;
    if (photoUrl && img?.complete && img.naturalWidth > 0 && loadedUrl !== photoUrl && failedUrl !== photoUrl) {
      setLoadedUrl(photoUrl);
      return;
    }
    if (photoUrl && imageStatus !== "loaded" && imageStatus !== "error") {
      return;
    }
    const hasPanels = Boolean(panels?.length && groundAlign && groundFrame);
    if (!hasTitleBoundary && !hasPanels) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    const photoReady =
      Boolean(photoUrl) &&
      imageStatus === "loaded" &&
      Boolean(img?.naturalWidth && img.naturalHeight);

    if (!photoReady || !img) return;

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const fromUrl = parseStaticMapView(satelliteImageUrl);
    const requested = parseStaticMapSize(fromUrl?.size ?? mapSize);
    const center = fromUrl?.center ?? mapCenter ?? insights.center;
    const zoom = fromUrl?.zoom && fromUrl.zoom > 0 ? fromUrl.zoom : mapZoom && mapZoom > 0 ? mapZoom : 20;
    if (panels?.length && groundAlign && groundFrame) {
      const quads = panels.flatMap((panel) => {
        const pixels = panelFootprintPixels(panel, groundAlign, groundFrame, {
          center,
          zoom,
          width: canvas.width,
          height: canvas.height,
          requested,
        });
        return pixels ? [pixels] : [];
      });
      drawPanelQuads(ctx, quads);
    }
    if (hasTitleBoundary) {
      const project: Projector = (lat, lng) =>
        latLngToStaticMapPixel(lat, lng, center, zoom, canvas.width, canvas.height, requested);
      drawOverlays(ctx, project, insights, propertyBoundary);
    }
  }, [
    insights,
    propertyBoundary,
    viableRoofPolygons,
    hasTitleBoundary,
    photoUrl,
    imageStatus,
    loadedUrl,
    failedUrl,
    mapCenter,
    mapZoom,
    mapSize,
    satelliteImageUrl,
    panels,
    groundAlign,
    groundFrame,
  ]);

  const roofSegmentStats =
    viableRoofPolygons?.features.map((feature) => {
      const props = feature.properties;
      return {
        facing: props.facing || "Unknown",
        area_m2: props.area_m2 || 0,
        pitch_degrees: props.pitch_degrees || 0,
        can_fit_panels: props.can_fit_panels || 0,
      };
    }) || [];

  const countedSegments = roofSegmentStats.filter((segment) => segment.can_fit_panels >= 2);
  const totalPanelCapacity = countedSegments.reduce((sum, s) => sum + s.can_fit_panels, 0);
  const showPhoto = Boolean(photoUrl) && imageStatus !== "error";
  const showSpinner = showPhoto && imageStatus !== "loaded";
  const canReviewOutline = outlineSource === "satellite_mask" && Boolean(address?.trim()) && Boolean(photoUrl) && viableRoofCount > 0;

  const submitReview = async (verdict: "positive" | "negative") => {
    if (!canReviewOutline || !address || !photoUrl) return;
    setReviewError("");
    try {
      await review.mutateAsync({
        address: address.trim(),
        postcode: postcode?.trim() || undefined,
        verdict,
        satellite_image_url: satelliteImageUrl?.trim() || photoUrl,
        title_geojson: propertyBoundary ?? null,
        roof_geojson: viableRoofPolygons,
        segment_centres: insights.roof_segments?.map((segment) => ({
          segment_index: segment.segment_index,
          facing: segment.facing,
          latitude: segment.center?.latitude,
          longitude: segment.center?.longitude,
        })),
      });
      setReviewVerdict(verdict);
    } catch {
      setReviewError("Could not save this review. Please try again.");
    }
  };

  return (
    <div className={className}>
      <p className="text-sm text-[#666666] mb-3">
        {panels && panels.length > 0
          ? "Satellite view with your panel layout."
          : hasTitleBoundary
            ? "Satellite view showing your property boundary in red."
            : "Satellite view of your property."}
        {panels && panels.length > 0 && hasTitleBoundary ? " The property boundary is shown in red." : ""}
      </p>
      <div className="space-y-3">
        <div className="relative min-h-[240px] rounded-lg overflow-hidden border border-[rgba(17,17,17,0.08)] bg-[#0b1220]">
          {showPhoto && (
            <img
              ref={imgRef}
              src={photoUrl}
              alt="Satellite view of your property"
              className="block w-full h-auto"
              onLoad={() => setLoadedUrl(photoUrl)}
              onError={() => setFailedUrl(photoUrl)}
            />
          )}
          <canvas
            ref={canvasRef}
            width={800}
            height={600}
            className={`absolute inset-0 h-full w-full pointer-events-none ${showSpinner ? "invisible" : ""}`}
          />

          {showSpinner && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0b1220]/80">
              <span className="flex items-center justify-center rounded-full bg-white p-3">
                <LimeSpinner size="lg" color="current" className="text-brand-navy" decorative />
              </span>
              <p className="text-sm text-white/70">Loading satellite view…</p>
            </div>
          )}

          {imageStatus === "error" && (
            <div className="absolute left-3 right-3 top-[4.75rem] rounded-md bg-black/65 px-2.5 py-1.5 text-xs text-white/80 backdrop-blur-sm">
              Satellite photo failed to load. Outlines are still shown on the map area.
            </div>
          )}

          {!photoUrl && (
            <div className="absolute left-3 right-3 top-[4.75rem] rounded-md bg-black/65 px-2.5 py-1.5 text-xs text-white/80 backdrop-blur-sm">
              Satellite photo unavailable — showing roof outlines only.
            </div>
          )}

          <CardinalCompass label="Compass. North is at the top of the satellite image." />

          <div className="absolute top-3 right-3 rounded-md bg-black/60 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
            {insights.imagery_quality} Quality · {insights.imagery_date.year}-
            {String(insights.imagery_date.month).padStart(2, "0")}
          </div>

          {totalPanelCapacity > 0 && (
            <div className="absolute bottom-3 left-3 rounded-md bg-black/60 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
              ~{totalPanelCapacity} panels (
              {(totalPanelCapacity * insights.panel_capacity_watts / 1000).toFixed(1)} kWp)
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {hasTitleBoundary && (
            <div className="flex items-center gap-2 rounded-lg border border-[rgba(17,17,17,0.08)] bg-white px-3 py-2">
              <div className="h-4 w-4 shrink-0 rounded border-2 border-red-600 bg-red-600/15" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-[#111111]">Property boundary</div>
                <div className="text-[10px] text-[#666666]">Freehold title</div>
              </div>
            </div>
          )}
        </div>

        {/* {roofSegmentStats.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {roofSegmentStats.map((segment, i) => (
              <div
                key={i}
                className="flex items-center gap-2 rounded-lg border border-[rgba(17,17,17,0.08)] bg-white px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-[#111111]">{segment.facing}</div>
                  <div className="text-[10px] text-[#666666]">
                    {segment.pitch_degrees.toFixed(0)}° · ~{segment.can_fit_panels} panels
                  </div>
                </div>
              </div>
            ))}
          </div>
        )} */}

        {/* {hasTitleBoundary && (
          <div className="rounded-lg border border-[#D4FF00]/30 bg-[#D4FF00]/5 px-3 py-2.5">
            <div className="flex items-center gap-2 mb-1">
              <div className="h-1.5 w-1.5 rounded-full bg-[#D4FF00] animate-pulse" />
              <span className="text-[10px] font-semibold text-[#0E9E77] uppercase tracking-wider">
                Property title clipping
              </span>
            </div>
            <p className="text-xs text-[#666666]">
              Roof planes are clipped to your property boundary. The panel count includes only
              planes with room for 2 or more panels.
            </p>
          </div>
        )} */}

        {canReviewOutline && (
          <div className="rounded-lg border border-[rgba(17,17,17,0.08)] bg-white px-3 py-2.5">
            <p className="text-xs font-semibold text-[#111111] mb-2">Does this roof outline look right?</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={review.isPending}
                onClick={() => submitReview("positive")}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                  reviewVerdict === "positive"
                    ? "bg-[#0E9E77] text-white"
                    : "bg-[#D4FF00]/40 text-[#111111]"
                }`}
              >
                Outline looks right
              </button>
              <button
                type="button"
                disabled={review.isPending}
                onClick={() => submitReview("negative")}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                  reviewVerdict === "negative"
                    ? "bg-[#111111] text-white"
                    : "border border-[rgba(17,17,17,0.15)] text-[#111111]"
                }`}
              >
                Outline is off
              </button>
            </div>
            {reviewError && <p className="mt-2 text-xs text-orange-700">{reviewError}</p>}
          </div>
        )}

        {countedSegments.length === 0 && (
          <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 px-3 py-2.5">
            <p className="text-xs text-orange-700">
              No roof areas found with capacity for 2+ panels. This property may not be suitable
              for solar panel installation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function drawOverlays(
  ctx: CanvasRenderingContext2D,
  project: Projector,
  insights: GoogleSolarInsights,
  propertyBoundary: GeoJSONFeature | undefined,
) {
  if (propertyBoundary?.geometry) {
    drawGeometry(ctx, propertyBoundary.geometry, project, TITLE_STROKE, TITLE_FILL, 3);
  }

  const centerPos = project(insights.center.latitude, insights.center.longitude);
  ctx.fillStyle = "#dc2626";
  ctx.beginPath();
  ctx.arc(centerPos.x, centerPos.y, 4, 0, 2 * Math.PI);
  ctx.fill();
  ctx.strokeStyle = "white";
  ctx.lineWidth = 2;
  ctx.stroke();
}

function drawGeometry(
  ctx: CanvasRenderingContext2D,
  geometry: GeoJSONFeature["geometry"],
  project: Projector,
  stroke: string,
  fill: string,
  lineWidth: number,
) {
  drawPolygons(ctx, geoJsonPolygons(geometry), project, stroke, fill, lineWidth);
}

function drawPolygons(
  ctx: CanvasRenderingContext2D,
  polygons: number[][][][],
  project: Projector,
  stroke: string,
  fill: string,
  lineWidth: number,
) {
  ctx.fillStyle = fill;
  ctx.strokeStyle = stroke;
  ctx.lineWidth = lineWidth;
  ctx.lineJoin = "round";

  polygons.forEach((polygon) => {
    ctx.beginPath();
    polygon.forEach((ring) => {
      ring.forEach((coord, i) => {
        const [lng, lat] = coord;
        const pos = project(lat, lng);
        if (i === 0) ctx.moveTo(pos.x, pos.y);
        else ctx.lineTo(pos.x, pos.y);
      });
      ctx.closePath();
    });
    ctx.fill("evenodd");
    ctx.stroke();
  });
}
